//@flow
import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@apollo/react-hooks';
import { RichText } from 'prismic-reactjs';
import { Client } from '../../prismic-configuration';
import { PrismicBusinessDetail } from '../../types/prismic';
import { Q_BUSINESS_ABOUT } from '../../graphql/business';
import { useAuth, usePreview, useWindowSize } from '../../hooks';
import useScrollPosition from '../../helpers/useScrollPosition';
import {
  BusinessAbout,
  BusinessAdditional,
  BusinessContact,
  BusinessPress,
  BusinessProducts,
  BusinessRecommendations,
  BusinessSpecs,
} from '../../components/business';
import styles from './about.module.sass';
import { Routes } from '../../helpers/constants';
import Link from 'next/link';

type Props = {
  doc: PrismicBusinessDetail;
};

enum DetailsNav {
  SPECS,
  CONTACT,
}

const BusinessDetailPage = ({ doc }: Props) => {
  const { user } = useAuth();
  const { previewMode } = usePreview();
  const { windowWidth } = useWindowSize();
  const router = useRouter();
  const { slug } = router.query;
  const [hasAccess, setHasAccess] = useState(false);
  const [detailsNavigation, setDetailsNav] = useState(DetailsNav.SPECS);
  const [requestDetailsNavigation, setRequestDetailsNav] = useState(
    DetailsNav.SPECS
  );
  const [detailsFixed, setDetailsFixed] = useState(false);
  const [detailsBottom, setDetailsBottom] = useState(0);
  const [detailsScroll, setDetailsScroll] = useState(0);
  const [detailsHeight, setDetailsHeight] = useState(0);
  const detailsRef = useRef<HTMLDivElement>();
  const detailsInnerRef = useRef<HTMLDivElement>();
  const detailsContentRef = useRef<HTMLDivElement>();
  const scrollPosition = useScrollPosition();

  const { loading, error, data, refetch } = useQuery(Q_BUSINESS_ABOUT, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'network-only',
    variables: {
      slug,
    },
  });

  useEffect(() => {
    checkRestrictions();
  }, [user, data]);

  useEffect(() => {
    if (requestDetailsNavigation === detailsNavigation) return;
    setTimeout(() => {
      setDetailsNav(requestDetailsNavigation);
    }, 500);
  }, [requestDetailsNavigation]);

  useEffect(() => {
    if (windowWidth < 1024) {
      setDetailsScroll(0);
      setDetailsBottom(0);
      return;
    }
    updateLayout();
  }, [scrollPosition, windowWidth]);

  const updateLayout = () => {
    if (
      windowWidth < 1024 ||
      !detailsRef ||
      !detailsRef.current ||
      !detailsContentRef ||
      !detailsContentRef.current
    )
      return;

    if (detailsInnerRef && detailsInnerRef.current) {
      setDetailsHeight(detailsInnerRef.current.getBoundingClientRect().height);
    }

    const container = detailsRef.current;
    const content = detailsContentRef.current;
    const containerRect = container.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();
    const containerTop = containerRect.top;
    const containerHeight = containerRect.height;
    const contentHeight = contentRect.height;
    const containerMarginTop = 200;
    const marginBottom =
      contentHeight + containerMarginTop > containerHeight
        ? contentHeight - window.innerHeight + containerMarginTop
        : containerMarginTop;
    // const scrollPercentage =
    //   contentHeight + containerMarginTop > containerHeight
    //     ? ((containerTop /
    //         (window.innerHeight -
    //           containerHeight -
    //           containerMarginTop -
    //           contentHeight))
    //           - containerTop / window.innerHeight) *
    //         100
    //     : 0;

    let detailsTop =
      contentHeight + containerMarginTop > containerHeight
        ? containerHeight > window.innerHeight
          ? (containerTop / window.innerHeight) *
            (contentHeight - containerHeight + containerMarginTop)
          : (containerTop / containerHeight) * (window.innerHeight - contentHeight) -
            containerMarginTop
        : 0;

    if (detailsTop > 0) detailsTop = 0;
    setDetailsScroll(detailsTop);
    if (
      containerTop <= 0 &&
      contentRect.bottom > containerRect.bottom + contentHeight
    ) {
      setDetailsFixed(true);
      setDetailsBottom(marginBottom);
    } else {
      setDetailsFixed(false);
      setDetailsBottom(0);
    }
  };

  const checkRestrictions = () => {
    if (user && data) {
      const isOwner = user.id === data.business?.data?.ownerId;
      const isAdmin =
        user.data.adminRole && user.data.adminRole.toUpperCase() === 'ADMIN';
      const isCorporationUser =
        user.corporation && user.corporation.corporation.data.isValidated;

      if (isOwner || isCorporationUser || isAdmin) {
        setHasAccess(true);
      }
    } else {
      setHasAccess(false);
    }
  };

  // if (error) return `Error! ${error.message}`;
  if (error || loading) {
    return <div className={`full-width row ${styles.container}`} />;
  }

  const handleBusinessUpdate = () => {
    refetch();
  };

  const { not_found_header, not_found_body, not_found_button_label } = doc.data;

  if (!loading && !data.business) {
    return (
      <div className={`full-width ${styles.container} center`}>
        <section className="inner-width inner-width-padding column center text-center">
          <h2>{not_found_header}</h2>
          <RichText render={not_found_body} />
          <Link href={Routes.SHOP_BLACK_SEARCH}>
            <a className="button primary">{not_found_button_label}</a>
          </Link>
        </section>
      </div>
    );
  }

  const { headerImage, website, about } = data.business.data;
  const isOwner = user && user.id === data.business.data.ownerId;
  const isAdmin =
    user && user.data.adminRole && user.data.adminRole.toUpperCase() === 'ADMIN';
  const isEditable = isOwner || isAdmin;

  const hasProductImages = () => {
    if (!about || !about.products) return false;
    for (let i = 0; i < about.products.length; i += 1) {
      const imageData = about.products[i];
      if (imageData && imageData.image && imageData.image.length) return true;
    }

    return false;
  };

  const hasAdditionalProducts =
    about &&
    ((about.additionalContent &&
      (about.additionalContent.header.length ||
        about.additionalContent.description.length)) ||
      hasProductImages());

  return (
    <>
      <section className={`inner-width inner-width-padding ${styles.about}`}>
        <BusinessAbout
          copy={doc.data}
          previewMode={previewMode}
          businessId={data.business.id}
          data={about}
          showInstagram={!hasAdditionalProducts}
          headerImage={headerImage}
          website={website}
          isEditable={isEditable}
          onUpdate={handleBusinessUpdate}
        />
      </section>
      {(hasAdditionalProducts || (isEditable && !previewMode)) && (
        <section className="full-width center no-overflow">
          <div className={`inner-width inner-width-padding ${styles.gallery}`}>
            <div className={styles.more}>
              <BusinessAdditional
                copy={doc.data}
                previewMode={previewMode}
                businessId={data.business.id}
                data={about ? about.additionalContent : null}
                instagram={about ? about.instagram : null}
                showInstagram={hasAdditionalProducts}
                isEditable={isEditable}
                onUpdate={handleBusinessUpdate}
              />
              <BusinessProducts
                previewMode={previewMode}
                businessId={data.business.id}
                data={about ? about.products : null}
                isEditable={isEditable}
                onUpdate={handleBusinessUpdate}
              />
            </div>
          </div>
        </section>
      )}
      <section className="inner-width inner-width-padding center">
        <BusinessPress
          copy={doc.data}
          previewMode={previewMode}
          businessId={data.business.id}
          data={about ? about.press : null}
          isEditable={isEditable}
          onUpdate={handleBusinessUpdate}
        />
      </section>
      {hasAccess && (
        <section
          ref={detailsRef}
          className="inner-width inner-width-padding"
          style={{
            height: detailsFixed ? '100vh' : 'auto',
            marginBottom: `${detailsBottom}px`,
          }}
        >
          <article
            className={`${styles.details} ${
              detailsFixed ? 'inner-width-padding' : ''
            }`}
            style={{
              position: detailsFixed ? 'fixed' : 'relative',
              top: detailsFixed ? '50px' : 0,
            }}
            data-selected={requestDetailsNavigation}
          >
            <div className={`${styles.ratio}`}>
              <div ref={detailsInnerRef} className={styles.inner}>
                <div className={styles.navbg}>
                  <div
                    className={styles.gradient}
                    style={{ height: `${detailsHeight}px` }}
                  />
                </div>
                <nav className={styles.nav}>
                  <button onClick={() => setRequestDetailsNav(DetailsNav.SPECS)}>
                    Details
                  </button>
                  <button onClick={() => setRequestDetailsNav(DetailsNav.CONTACT)}>
                    Contact
                  </button>
                </nav>
                {isEditable && !previewMode && (
                  <div className={styles.note}>
                    <RichText render={doc.data.specs_retailer_facing} />
                  </div>
                )}
                <div
                  ref={detailsContentRef}
                  className={styles.content}
                  style={{
                    transform: `translate3d(0, ${detailsScroll}px, 0)`,
                    opacity: requestDetailsNavigation !== detailsNavigation ? 0 : 1,
                  }}
                >
                  {detailsNavigation === DetailsNav.SPECS && (
                    <BusinessSpecs
                      businessId={data.business.id}
                      doc={doc}
                      onLayoutUpdate={updateLayout}
                    />
                  )}
                  {detailsNavigation === DetailsNav.CONTACT && (
                    <BusinessContact
                      businessId={data.business.id}
                      doc={doc}
                      onLayoutUpdate={updateLayout}
                    />
                  )}
                </div>
              </div>
            </div>
          </article>
        </section>
      )}
      {data.business.recommendedBusinesses &&
        data.business.recommendedBusinesses.length > 0 && (
          <section className="inner-width inner-width-padding center">
            <BusinessRecommendations
              label={doc.data.recommended_business_label}
              data={data.business.recommendedBusinesses}
            />
          </section>
        )}
    </>
  );
};

export async function getServerSideProps() {
  const client = Client();
  const doc = await client.getSingle('business-detail', {});

  return {
    props: {
      doc,
    },
  };
}

export default BusinessDetailPage;
